/**
 * SIC (Subjective Intentional Constraint) Prompts
 *
 * Prompts for detecting authorship constraint and AI generation indicators.
 *
 * @module config/prompts/sic-prompts
 */
// ═══════════════════════════════════════════════════════════════════════════
// SIC GUARDRAILS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Guardrails appended to all SIC prompts
 */
export const SIC_GUARDRAILS = `
CRITICAL RULES:
- Output ONLY valid JSON. No markdown, no explanation, no preamble.
- Do NOT evaluate style, aesthetics, or quality.
- Do NOT suggest improvements or rewrites.
- Focus ONLY on detecting constraint traces as specified.
- Every claim must cite specific evidence from the text.
- If uncertain, say "insufficient evidence" rather than guessing.
`;
// ═══════════════════════════════════════════════════════════════════════════
// SIC PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export const SIC_GENRE_DETECTION = {
    id: 'SIC_GENRE_DETECTION',
    name: 'SIC Genre Detection',
    description: 'Classify text genre for SIC analysis calibration (Pass 0)',
    template: `You are a genre classifier. Analyze the text and determine its primary genre.

Your task:
1. Read the text carefully
2. Identify the primary genre from: narrative, argument, technical, legal, marketing, unknown
3. Note any genre mixing or ambiguity

Text:
{{text}}

Output JSON only:
{
  "genre": "narrative" | "argument" | "technical" | "legal" | "marketing" | "unknown",
  "confidence": 0.0-1.0,
  "notes": "brief explanation of genre signals"
}
${SIC_GUARDRAILS}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.2,
        maxTokens: 512,
    },
    version: 1,
    usedBy: ['sic-analyzer'],
};
export const SIC_EXTRACTOR = {
    id: 'SIC_EXTRACTOR',
    name: 'SIC Evidence Extractor',
    description: 'Extract evidence of subjective intentional constraint (Pass 1)',
    template: `You are a constraint detector. Your task is to find evidence of SUBJECTIVE INTENTIONAL CONSTRAINT in the text.

Subjective Intentional Constraint measures the cost of authorship: traces of commitment, tradeoff, irreversibility, and situated stakes. It is NOT about style or quality.

For EACH feature category, extract relevant quotes (max 25 words each) with rationale:

POSITIVE FEATURES (indicators of human authorship):

1. commitment_irreversibility
   - Concrete decisions with consequences
   - Claims that narrow future options
   - "Humans trap themselves. LLMs keep exits open."

2. epistemic_risk_uncertainty
   - Being wrong, surprises, ignorance that mattered
   - NOT just hedging words, but genuine not-knowing with stakes
   - "I thought X but Y", discoveries that cost something

3. time_pressure_tradeoffs
   - Urgency, deadlines, asymmetric time awareness
   - "suddenly", "before I could", "too late"

4. situatedness_body_social
   - Embodied risk, social cost, friction
   - Physical presence, social exposure

5. scar_tissue_specificity
   - PERSISTENT residue in the PRESENT
   - Physical involuntary reactions: flinch, wince, freeze
   - Temporal persistence: "still", "even now", "years later"
   - NOT formulaic apology language

6. bounded_viewpoint
   - Non-omniscient narration
   - Limited perspective, acknowledged gaps

NEGATIVE FEATURES (indicators of AI generation):

7. anti_smoothing (high score = GOOD)
   - REFUSAL OF SYMMETRY
   - HIGH: Asymmetric commitment, closure of alternatives
   - LOW: "Valid arguments on both sides", performed balance

8. meta_contamination
   - Preambles, "EDIT:", roleplay wrappers
   - "In conclusion", "It is important to note"

Text:
{{text}}

Output JSON only:
{
  "features": {
    "commitment_irreversibility": {
      "evidence": [{"quote": "...", "rationale": "..."}],
      "signal_strength": "none" | "weak" | "moderate" | "strong"
    }
  },
  "preliminary_notes": "overall observations"
}
${SIC_GUARDRAILS}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['sic-analyzer'],
};
export const SIC_JUDGE = {
    id: 'SIC_JUDGE',
    name: 'SIC Judge',
    description: 'Score constraint evidence and calculate final SIC score (Pass 2)',
    template: `You are a constraint judge. Given extracted evidence, score each feature and provide final assessment.

GENRE CONTEXT: {{genreContext}}

Your task:
1. Review the extracted evidence for each feature
2. Score each feature 0-100 based on evidence strength
3. Apply genre-appropriate calibration
4. Identify key inflection points (collapse moments)
5. Calculate overall SIC score

SCORING GUIDELINES:
- 0-20: No evidence or very weak signals
- 21-40: Some evidence but inconsistent
- 41-60: Moderate evidence, clear constraint traces
- 61-80: Strong evidence, multiple constraint types
- 81-100: Exceptional, load-bearing constraint throughout

INFLECTION POINTS:
Collapse moments where interpretive freedom reduces.
Types: commitment, reversal, reframe, stakes, constraint-reveal

Evidence:
{{extractedEvidence}}

Output JSON only:
{
  "features": {
    "commitment_irreversibility": {
      "score": 0-100,
      "notes": "brief scoring rationale",
      "evidence": [{"quote": "...", "rationale": "..."}]
    }
  },
  "inflectionPoints": [
    {
      "chunkId": "chunk_0",
      "kind": "commitment",
      "quote": "the specific sentence",
      "whyItMatters": "why this reduces degrees of freedom"
    }
  ],
  "sicScore": 0-100,
  "aiProbability": 0.0-1.0,
  "diagnostics": {
    "genreBaselineUsed": true/false,
    "corporateBureaucratRisk": true/false,
    "highFluencyLowCommitmentPattern": true/false
  },
  "notes": "human-readable summary"
}
${SIC_GUARDRAILS}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['sic-analyzer'],
};
export const SIC_STYLE_CHECK_EXTRACTOR = {
    id: 'SIC_STYLE_CHECK_EXTRACTOR',
    name: 'Style Check Extractor',
    description: 'Extract style features for comparison against profile',
    template: `You are a style analyzer. Compare the text against the provided style profile.

Style Profile:
{{styleProfile}}

Text:
{{text}}

Output JSON only:
{
  "matches": [{"pattern": "...", "evidence": "...", "strength": 0.0-1.0}],
  "deviations": [{"expected": "...", "actual": "...", "severity": "minor" | "moderate" | "major"}],
  "metrics": {
    "avgSentenceLength": number,
    "vocabularyLevel": "basic" | "intermediate" | "advanced",
    "formalityLevel": "informal" | "neutral" | "formal"
  }
}
${SIC_GUARDRAILS}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['sic-analyzer'],
};
export const SIC_STYLE_CHECK_JUDGE = {
    id: 'SIC_STYLE_CHECK_JUDGE',
    name: 'Style Check Judge',
    description: 'Score overall style consistency',
    template: `You are a style consistency judge. Score the overall consistency.

Extraction Results:
{{extractionResults}}

Output JSON only:
{
  "consistencyScore": 0-100,
  "profileMatchScore": 0-100,
  "deviations": ["human-readable deviation descriptions"],
  "metrics": {
    "perplexity": number or null,
    "burstiness": number or null,
    "avgSentenceLength": number,
    "typeTokenRatio": number or null
  }
}
${SIC_GUARDRAILS}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['sic-analyzer'],
};
export const SIC_PROFILE_VETTING = {
    id: 'SIC_PROFILE_VETTING',
    name: 'Profile Vetting',
    description: 'Evaluate if text is suitable for extracting a writing profile',
    template: `You are a profile source evaluator. Determine if this text is suitable for extracting a writing profile.

Text:
{{text}}

Output JSON only:
{
  "suitable": true/false,
  "qualityScore": 0-100,
  "sicScore": 0-100,
  "concerns": ["list of specific concerns"],
  "recommendations": ["suggestions for better samples"]
}
${SIC_GUARDRAILS}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['sic-analyzer'],
};
export const SIC_PROMPTS = [
    SIC_GENRE_DETECTION,
    SIC_EXTRACTOR,
    SIC_JUDGE,
    SIC_STYLE_CHECK_EXTRACTOR,
    SIC_STYLE_CHECK_JUDGE,
    SIC_PROFILE_VETTING,
];
//# sourceMappingURL=sic-prompts.js.map